import React, { useMemo, useState } from "react";
import moment from "moment";
import { toast } from "react-hot-toast";
import {
  ButtonComp,
  CardComp,
  ModalCompAdvanceUpdate,
  TableCompData,
  TabsComp,
} from "../../../components";
import EmptyTable from "../../../components/Common/EmptyTable";
import TableSkeletonLoader from "../../../components/Common/TableSkeletonLoader";
import TablePagination from "../../../components/Table/Pagination";
import PageWrapper from "../modules/PageWrapper";
import { formatMoney } from "../../../utils/currencyFormat";
import { convertToTitleCase } from "../../../utils/helpers";
import {
  useConfirmMemberGuarantorRequestMutation,
  useDeclineMemberGuarantorRequestMutation,
  useGetMemberGuarantorRequestsQuery,
} from "../../../store/member/member-loan";

const statusBadgeStyle = (status) => {
  const normalized = (status || "").toUpperCase();
  if (normalized === "CONFIRMED") return { background: "#19BC31", color: "#fff" };
  if (normalized === "DECLINED") return { background: "#FF4343", color: "#fff" };
  if (normalized === "PENDING") return { background: "#FFA500", color: "#fff" };
  return { background: "#A1A0A0", color: "#fff" };
};

function GuarantorRequestsTable({
  statusFilter,
  onApprove,
  onDecline,
}) {
  const [page, setPage] = useState(1);
  const { data, isLoading, isFetching } = useGetMemberGuarantorRequestsQuery({
    status: statusFilter,
    page,
    limit: 20,
  });

  const rows = data?.guarantor_requests || [];
  const pagination = data?.pagination;
  const loading = isLoading || isFetching;

  const columns = useMemo(
    () => [
      {
        name: "Applicant",
        selector: (row) => row?.loan_request?.member?.name || "N/A",
        cell: (row) => (
          <span>{row?.loan_request?.member?.name || "N/A"}</span>
        ),
        grow: 1.2,
      },
      {
        name: "Membership Number",
        selector: (row) =>
          row?.loan_request?.member?.member_no || "N/A",
        cell: (row) => (
          <span>{row?.loan_request?.member?.member_no || "N/A"}</span>
        ),
      },
      {
        name: "Amount",
        selector: (row) => row?.loan_request?.amount || 0,
        cell: (row) => {
          const amount = Number(row?.loan_request?.amount || 0);
          return <span>{formatMoney(amount.toFixed(2))}</span>;
        },
      },
      {
        name: "Loan Type",
        selector: (row) => row?.loan_request?.loan_type?.name || "N/A",
        cell: (row) => (
          <span>{row?.loan_request?.loan_type?.name || "N/A"}</span>
        ),
      },
      {
        name: "Requested Date",
        selector: (row) => row?.created_at,
        cell: (row) => (
          <span>
            {row?.created_at
              ? moment(row.created_at).format("DD-MMM-YYYY")
              : "N/A"}
          </span>
        ),
      },
      {
        name: "Status",
        selector: (row) => row?.status,
        cell: (row) => {
          const status = row?.status || "PENDING";
          return (
            <span
              className="rounded-pill px-3 py-1 text-white"
              style={{
                ...statusBadgeStyle(status),
                minWidth: "90px",
                display: "inline-block",
                textAlign: "center",
                fontSize: 12,
              }}
            >
              {convertToTitleCase(status)}
            </span>
          );
        },
      },
      {
        name: "Action",
        cell: (row) => {
          const isPending = (row?.status || "").toUpperCase() === "PENDING";
          if (!isPending) {
            return <span className="text-muted">—</span>;
          }
          return (
            <div className="d-flex gap-2 flex-wrap">
              <ButtonComp
                btnText="Approve"
                btnClassName="btn-sm"
                ButtonInlineStyle={{
                  whiteSpace: "nowrap",
                  minWidth: "fit-content",
                  backgroundColor: "#19BC31",
                  borderColor: "#19BC31",
                  color: "#fff",
                }}
                onClick={() => onApprove(row)}
              />
              <ButtonComp
                btnText="Decline"
                btnClassName="btn-sm"
                ButtonInlineStyle={{
                  whiteSpace: "nowrap",
                  minWidth: "fit-content",
                  backgroundColor: "#FF4343",
                  borderColor: "#FF4343",
                  color: "#fff",
                }}
                onClick={() => onDecline(row)}
              />
            </div>
          );
        },
        ignoreRowClick: true,
        allowOverflow: true,
        button: true,
        minWidth: "200px",
      },
    ],
    [onApprove, onDecline]
  );

  return (
    <div className="mt-3">
      <TableCompData
        marginBottom="1rem"
        columns={columns}
        data={rows}
        progressPending={loading}
        progressComponent={<TableSkeletonLoader rows={8} columns={7} />}
        message={
          !loading && (
            <EmptyTable
              Message={
                statusFilter === "pending"
                  ? "You have no pending guarantor requests"
                  : "You have no guarantor requests"
              }
            />
          )
        }
        cellPadding="12px"
      />
      {!loading && rows.length > 0 && pagination && (
        <TablePagination
          marginTop="0"
          handlePageChange={setPage}
          {...pagination}
        />
      )}
    </div>
  );
}

export default function GuarantorRequests() {
  const [modalPage, setModalPage] = useState("");
  const [selectedRequest, setSelectedRequest] = useState(null);

  const [confirmRequest, { isLoading: isConfirming }] =
    useConfirmMemberGuarantorRequestMutation();
  const [declineRequest, { isLoading: isDeclining }] =
    useDeclineMemberGuarantorRequestMutation();

  const openApprove = (request) => {
    setSelectedRequest(request);
    setModalPage("Approve Guarantor");
  };

  const openDecline = (request) => {
    setSelectedRequest(request);
    setModalPage("Decline Guarantor");
  };

  const handleConfirm = async () => {
    if (!selectedRequest?.id) return;
    try {
      const result = await confirmRequest(selectedRequest.id).unwrap();
      toast.success(result?.message || "Guarantor request approved");
      setModalPage("");
      setSelectedRequest(null);
    } catch (error) {
      toast.error(
        error?.data?.message || "Failed to approve guarantor request"
      );
    }
  };

  const handleDecline = async () => {
    if (!selectedRequest?.id) return;
    try {
      const result = await declineRequest(selectedRequest.id).unwrap();
      toast.success(result?.message || "Guarantor request declined");
      setModalPage("");
      setSelectedRequest(null);
    } catch (error) {
      toast.error(
        error?.data?.message || "Failed to decline guarantor request"
      );
    }
  };

  const useTab = [
    {
      name: "Pending",
      component: (
        <GuarantorRequestsTable
          statusFilter="pending"
          onApprove={openApprove}
          onDecline={openDecline}
        />
      ),
    },
    {
      name: "All",
      component: (
        <GuarantorRequestsTable
          statusFilter="all"
          onApprove={openApprove}
          onDecline={openDecline}
        />
      ),
    },
  ];

  const modalComponents = [
    {
      name: "Approve Guarantor",
      component: (
        <div className="space-y-4 py-2">
          <p className="mb-0">
            Approve as guarantor for{" "}
            <strong>
              {selectedRequest?.loan_request?.member?.name || "this member"}
            </strong>
            {selectedRequest?.loan_request?.amount != null && (
              <>
                {" "}
                (
                {formatMoney(
                  Number(selectedRequest.loan_request.amount).toFixed(2)
                )}
                )?
              </>
            )}
          </p>
          <div className="d-flex gap-3 justify-content-end mt-4">
            <ButtonComp
              btnText="Cancel"
              onClick={() => setModalPage("")}
              ButtonInlineStyle={{ minWidth: 120 }}
            />
            <ButtonComp
              btnText="Approve"
              loading={isConfirming}
              onClick={handleConfirm}
              ButtonInlineStyle={{
                minWidth: 120,
                backgroundColor: "#19BC31",
                borderColor: "#19BC31",
                color: "#fff",
              }}
            />
          </div>
        </div>
      ),
    },
    {
      name: "Decline Guarantor",
      component: (
        <div className="space-y-4 py-2">
          <p className="mb-0">
            Decline the guarantor request for{" "}
            <strong>
              {selectedRequest?.loan_request?.member?.name || "this member"}
            </strong>
            ?
          </p>
          <div className="d-flex gap-3 justify-content-end mt-4">
            <ButtonComp
              btnText="Cancel"
              onClick={() => setModalPage("")}
              ButtonInlineStyle={{ minWidth: 120 }}
            />
            <ButtonComp
              btnText="Decline"
              loading={isDeclining}
              onClick={handleDecline}
              ButtonInlineStyle={{
                minWidth: 120,
                backgroundColor: "#FF4343",
                borderColor: "#FF4343",
                color: "#fff",
              }}
            />
          </div>
        </div>
      ),
    },
  ];

  return (
    <PageWrapper title="Guarantor Requests">
      <section className="mt-4">
        <CardComp className="py-3 px-3">
          <p className="text-muted mb-3 px-1">
            Review loan applications where you have been selected as a
            guarantor. Approve or decline pending requests.
          </p>
          <TabsComp
            TabArray={useTab}
            justifyContent="justify-content-start"
            tabClassName="px-0"
            TabArrayClassName="px-1"
          />
        </CardComp>
      </section>

      <ModalCompAdvanceUpdate
        pageName={modalPage}
        handleClose={() => {
          setModalPage("");
          setSelectedRequest(null);
        }}
        close
        centered
        size="md"
        modalTitle={modalPage}
        arrayComp={modalComponents}
      />
    </PageWrapper>
  );
}
